import { Box } from '@mui/material'
import React, { lazy } from 'react'
import { Route, Routes } from 'react-router-dom'

import { ProfilePage } from '@/pages/Profile/ProfilePage'
import { SettingsPage } from '@/pages/Settings/SettingsPage'
import { TermsAndConditions } from '@/features/termsAndConditions/ui/TermsAndConditions'
import {
    TCPage,
    faqPagePath,
    profilePagePath,
    settingsPagePath,
    termsOfServicePagePath,
    newsletterCancelPath,
    webPath,
} from '@/shared/constants/appConstants'
import { epgPagePath } from '@/shared/constants/epgConstants'
import { vodPagePath } from '@/shared/constants/vodConstants'
import { homePagePath } from '@/shared/constants/homeConstants'
import { useMainScreenLogic } from '@/pages/mainScreen/model'
import { ContentSuspense } from '@/shared/ui/ContentSuspense'
import { GetVODDetailsProvider } from '@/features/VOD/context'
import HomeContent from '@/pages/home/HomeContent'
import { FAQ } from '../FAQ/FAQ'
import { TermsOfService } from '@/pages/TermsOfService/TermsOfService'
import { NewsletterContent } from '@/pages/Settings/NewsletterContent'
import CookiesModal from '@/pages/mainScreen/CookiesModal'

// Lazy-loaded components
const WebContent = lazy(
    () => import('../../components/web/components/WebContent')
)
const TvContent = lazy(() => import('../../components/tv/components/TvContent'))
const EpgContent = lazy(
    () => import('../../components/epg/components/EpgContent')
)
const VodContent = lazy(
    () => import('../../features/VOD/components/VodContent')
)

export const MainScreen: React.FC = (): JSX.Element => {
    const {
        isTVisible,
        isVodContentVisible,
        pathname,
        isVodContentPlaying,
        openModal,
        handleClose,
    } = useMainScreenLogic()

    const mainScreenContainerStyle = {
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.5s ease-in-out',
        maxHeight: '100%',
        height: '100%',
        overflow: { xs: 'auto', sm: 'hidden' },
        position: 'relative',
    }

    return (
        <Box sx={mainScreenContainerStyle}>
            <Box
                sx={{
                    position: !isVodContentVisible ? 'relative' : 'static',
                }}>
                {/* Display TV Content if applicable */}
                {isTVisible && !isVodContentPlaying && <TvContent />}

                {/* Routes wrapped in ContentSuspense */}
                <ContentSuspense actualPath={pathname}>
                    <Routes>
                        <Route path={homePagePath} element={<HomeContent />} />
                        <Route path={epgPagePath} element={<EpgContent />} />
                        <Route
                            path={newsletterCancelPath}
                            element={<NewsletterContent />}
                        />
                        <Route path={webPath} element={<WebContent />} />
                        <Route
                            path={vodPagePath}
                            element={
                                <GetVODDetailsProvider>
                                    <VodContent />
                                </GetVODDetailsProvider>
                            }
                        />
                        <Route
                            path={profilePagePath}
                            element={<ProfilePage />}
                        />
                        <Route
                            path={settingsPagePath}
                            element={<SettingsPage />}
                        />
                        <Route path={faqPagePath} element={<FAQ />} />
                        <Route path={TCPage} element={<TermsAndConditions />} />
                        <Route
                            path={termsOfServicePagePath}
                            element={<TermsOfService />}
                        />
                        {/* Catch-all route for undefined paths */}
                        <Route path="/*" element={<HomeContent />} />
                    </Routes>
                </ContentSuspense>
            </Box>

            {/* Cookies modal */}
            <CookiesModal open={openModal} handleClose={handleClose} />
        </Box>
    )
}
